import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import useRedirect from "@shared/hooks/useRedirect";
import useUserBasedRefundProtectionService from "../../../data/services/refund-protection/useUserBasedRefundProtectionService";
import { BaseRefundProtectionClaim } from "../BaseRefundProtectionClaim.web";
import usePublicBasedRefundProtectionService from "../../../data/services/refund-protection/usePublicBasedRefundProtectionService";

type TeakRefundProtectionClaimProps = {
  transactionId: string;
  basketId: number;
  uuid1: string;
  uuid2: string;
  isBasketClaimed: boolean;
  withActionButtons?: boolean;
};

export function TeakRefundProtectionClaim({
  transactionId,
  basketId,
  uuid1,
  uuid2,
  isBasketClaimed,
  withActionButtons = true,
}: TeakRefundProtectionClaimProps) {
  const { redirect } = useRedirect();
  const { isLoggedIn } = useUserSessionAuthState();

  // Use the internal metadata endpoint to obtain details from the protection provider Teak.
  // This is because Teak uses short-lived dynamic tokens on their endpoints.
  const { useGetMetadata } = useUserBasedRefundProtectionService();
  const { useGetMetadata: usePublicBasedGetMetadata } =
    usePublicBasedRefundProtectionService();

  const { data, error } = useGetMetadata(
    {
      transaction_id: transactionId,
    },
    { enabled: isLoggedIn && isBasketClaimed }
  );

  const { data: publicBasedData, error: publicBasedError } =
    usePublicBasedGetMetadata(
      {
        uuid1,
        uuid2,
        id: Number(basketId),
      },
      { enabled: !isLoggedIn || !isBasketClaimed }
    );

  const displayData = isLoggedIn && isBasketClaimed ? data : publicBasedData;
  const displayError = isLoggedIn && isBasketClaimed ? error : publicBasedError;

  return (
    <BaseRefundProtectionClaim
      // Teak restricts putting the `Teak` branding on any consumer-facing assets.
      hasError={Boolean(displayError)}
      providerName=""
      onRefundRequest={
        displayData?.refund_request_url
          ? () => {
              redirect(displayData.refund_request_url, {
                asNewTab: true,
              });
            }
          : null
      }
      onCancelProtection={
        displayData?.cancellation_url
          ? () => {
              redirect(displayData.cancellation_url, {
                asNewTab: true,
              });
            }
          : null
      }
      refundTermsURL={displayData?.faq_url ?? null}
      withActionButtons={withActionButtons}
    />
  );
}
